// ── <ProbabilityPlot> — probability-over-time line for Forecast Tracking ──
//
// Extracted verbatim from the AppInner IIFE (App.jsx ~line 6624) that rendered
// the piecewise-constant past + smooth-transition future probability line.
// This is a behavior-preserving extraction: ALL logic is ported unchanged from
// the IIFE; only the closure values are re-sourced from props (`ctx`).
//
// The component is UNGATED — the caller (App.jsx) is responsible for:
//   • checking `forecastTracking && active?.history?.length > 0 &&
//     active?.contract?.resolution_date && active?.contract?.probability != null`
//   before rendering this component (matches the IIFE's guard exactly).
//
// Module-level deps imported directly (same file used `import … from` in App.jsx):
//   C               — lib/colors.js  (mutable color object, assigned by theme)
//   R_START         — lib/timeline/geometry.js
//
// Runtime values come from `ctx` (AppInner-scoped at render time):
//   active          — the active forecast run
//   effectiveToday  — Date (wall-clock or mock)
//   SPINE_Y         — px Y of the primary spine (AppInner-computed from viewport)
//   STREAM_HALF     — half-height of the YES/NO channels (50 — AppInner const)
//   viewLeft        — scroll offset in days
//   ppd             — pixels-per-day zoom
//   vw              — viewport width in px
//   axisHoverX      — cursor X relative to timeline div, or null
//   hov             — hover key string (or null)
//   hovPos          — { x, y } cursor position
//   setHov          — setter for hov
//   setHovPos       — setter for hovPos

import { themeColors } from "../../lib/colors.js";
import { R_START } from "../../lib/timeline/geometry.js";
import { buildReforecastMoments } from "../../lib/forecast/reportPointer.js";
import {
  bucketLabel,
  extractDistribution,
  resolvedBucketIndex,
} from "../../lib/forecast/multiway.js";
import {
  buildDistributionMoments,
  seriesForLabel,
  leadingLabel,
} from "../../lib/forecast/binSeries.js";
import { buildStepPath, collapseNarrowReversals } from "../../lib/forecast/stepPath.js";
import { fmtPct } from "../../lib/forecast/formatPct.js";

export default function ProbabilityPlot({ ctx }) {
  const {
    active,
    effectiveToday,
    SPINE_Y,
    STREAM_HALF,
    viewLeft,
    ppd,
    vw,
    hov,
    hovPos,
    setHov,
    setHovPos,
    truncateMs,
    asyncMoments,
    theme,
    resolvedCapMs,
    // Defaults ON, matching the flag it carries (chatboard-outcome-history-lines):
    // a caller that does not pass it gets the shipped behaviour, not the old one.
    perOutcome = true,
    allMultiway,
    displayContract,
    hoveredBinLabel,
    verdictStatus,
    verdict,
  } = ctx;
  // SVG chart: line colors are applied as `stroke` presentation attributes, and are
  // also used as the keys that batch same-colored subpaths together, so read the
  // raw-hex palette rather than the frozen var-based `C` — var() does not resolve in
  // an SVG presentation attribute.
  const C = themeColors(theme);

  // ── Which line to draw ──────────────────────────────────────────────────────
  // The plot follows whatever the resolution marker draws. Where the marker is the
  // distribution bars, each outcome has its own history and the line traces ONE of
  // them — the bar the user is hovering, or the current leader when they are not.
  // Where the marker is still the probability pie (a yes/no question with All
  // Multiway off), there are no bars to hover and the line stays the P(YES) line it
  // has always been — otherwise it would sit at a different height than the pie on
  // the same forecast, with nothing on screen to account for the gap.
  // TWO distributions, and the split matters once a question's outcomes change shape
  // over its life (a bin added, a bin dropped):
  //
  //  • `dist` — the LIVE contract's. It is what the newest reforecast produced, so it
  //    is the series' last data point. It must stay live even while rewound: the trace
  //    is built whole and then CLIPPED at the cap, never rebuilt from the vintage.
  //  • `selectionDist` — the DISPLAYED contract's (the rewound vintage's, when
  //    scrubbed). It decides which outcomes are selectable and which one leads, because
  //    the marker the user is hovering is drawn from that same vintage. Reading the live
  //    one here meant hovering a bin that existed THEN but not now silently fell back to
  //    today's leader.
  const dist = extractDistribution(active.contract, {
    allMultiway,
    probability: active.contract.probability,
  });
  const selectionContract = displayContract ?? active.contract;
  const selectionDist = extractDistribution(selectionContract, {
    allMultiway,
    probability: selectionContract.probability,
  });
  // Default to the outcome that OCCURRED once a verdict names one, so a settled
  // forecast traces the outcome the marker has just checked off in gold rather than
  // whichever bucket happened to be leading when the last forecast landed. Nothing
  // names a bucket on a multi-value resolution today, so in practice this is the
  // binary case; it starts applying to multi-value the day a resolution carries one.
  const resolvedIdx = selectionDist
    ? resolvedBucketIndex(selectionDist.buckets, selectionDist.kind, verdictStatus, verdict)
    : null;
  const defaultLabel =
    resolvedIdx != null
      ? (selectionDist.buckets[resolvedIdx]?.label ?? null)
      : leadingLabel(selectionDist?.buckets);
  // A hovered label only wins if the forecast ON SCREEN carries that outcome — the hover
  // state is held above the lane, so it can outlive a switch to another forecast or a
  // rewind to a vintage whose outcome space was different.
  const hoverIsReal =
    typeof hoveredBinLabel === "string" &&
    Array.isArray(selectionDist?.buckets) &&
    selectionDist.buckets.some((b) => b?.label === hoveredBinLabel);
  const binLabel = hoverIsReal ? hoveredBinLabel : defaultLabel;
  // Snapshot-rewind truncation: when the user is viewing a past reforecast
  // snapshot, the line stops at that snapshot's supersede moment (truncateMs) —
  // later movement is hidden ("as of then"). null → live: draw through today.
  if (active.createdAt == null) return null;
  const todayMs = effectiveToday.getTime();
  // Effective right-edge cap for the trace, as the MIN of two independent caps:
  //   • truncateMs — snapshot-rewind ("as of then"), null when live.
  //   • resolvedCapMs — the resolution moment, set once the question has resolved
  //     so the past trace stops at the pie instead of overshooting to today.
  // Either can be null; the trace is capped at whichever is set (the earlier one
  // when both are). Null → no cap (live, unresolved) → draw through today.
  let capMs = truncateMs != null ? truncateMs : null;
  if (resolvedCapMs != null) {
    capMs = capMs != null ? Math.min(capMs, resolvedCapMs) : resolvedCapMs;
  }
  // Unified reforecast series (RIGHT convention: each {atMs, p} = a forecast of p
  // made at atMs, held until the next moment). Merges manual ↻ snapshots
  // (active.history) with the async scheduled reforecasts (asyncMoments, fetched
  // per-pointer by useReportPointerSeries). Equivalent to the prior manual-only
  // checkpoint construction when asyncMoments is empty — see buildReforecastMoments.
  const scalarMoments = buildReforecastMoments(
    active.history,
    asyncMoments,
    active.contract.probability,
    active.createdAt,
  );
  // Skipped entirely when the per-outcome lines are off, so the flag costs nothing:
  // no per-bucket series are built and the plot falls back to the scalar line.
  const distMoments = perOutcome
    ? buildDistributionMoments({
        history: active.history,
        asyncMoments,
        currentBuckets: dist?.buckets ?? null,
        currentProbability: active.contract.probability,
        createdAt: active.createdAt,
        allMultiway,
      })
    : [];
  const binMoments = binLabel ? seriesForLabel(distMoments, binLabel) : [];
  // Fall back to the scalar line whenever there is no per-outcome history to draw
  // (no distribution, or a label with no vintages carrying it).
  const usingBins = binMoments.length > 0;
  const moments = usingBins ? binMoments : scalarMoments;
  // Y math — same as the resolution pie so the line lands on
  // the circle's center cleanly.
  const MAX_TRIGGER_R = 18;
  const channelOffset = STREAM_HALF * 0.55;
  const iconCenterAt1 = SPINE_Y - channelOffset - MAX_TRIGGER_R;
  const iconCenterAt0 = SPINE_Y + channelOffset + MAX_TRIGGER_R;
  const yAtProb = (p) => {
    // Segments use their ACTUAL historical probability, never a verdict override —
    // showing what was believed at each moment is the whole point of the trace.
    const safeP = Math.max(0, Math.min(1, p));
    return safeP >= 0.5
      ? SPINE_Y + (safeP - 0.5) * 2 * (iconCenterAt1 - SPINE_Y)
      : SPINE_Y + (0.5 - safeP) * 2 * (iconCenterAt0 - SPINE_Y);
  };
  const xAtMs = (ms) => {
    const days = (ms - R_START) / 86400000;
    return (days - viewLeft) * ppd;
  };
  // ── END-OF-FORECAST line ────────────────────────────────────────────────────
  // Where a resolved forecast STOPS. It is the seam of the resolved view: the trace
  // ends here (resolvedCapMs is the same moment it is capped at) and the resolution
  // marker's bars begin just to the right of it, so a reader can see that the
  // distribution concludes the line rather than floating beside it.
  //
  // Deliberately NOT full height like the TODAY and FORECAST lines. Those mark a
  // moment for the whole column — axis, signals, every lane — whereas this marks the
  // end of ONE line's data, so it is scoped to that line's band: the 0%–100% range
  // the trace can occupy (iconCenterAt0 … iconCenterAt1, the same two heights
  // `yAtProb` maps the extremes to), overhanging each limit by 15% of the band so it
  // brackets the plot instead of cutting the column.
  //
  // Null unless the verdict is showing: `resolvedCapMs` is already gated on that
  // upstream (it goes null while the user scrubs to a pre-resolution vintage), which
  // is what keeps this line and the marker appearing and disappearing together.
  //
  // Drawn only where the marker is the distribution BARS, which is what `dist` decides
  // (the same call the lane makes to choose bars over the pie). The bars sit just to
  // the right of the moment, so the line is a seam between line and marker; the pie is
  // CENTRED on it — by design, so the trace lands on the circle's middle — and a line
  // there would strike straight through it rather than mark its edge.
  const BAND_OVERHANG = 0.15;
  const bandH = iconCenterAt0 - iconCenterAt1;
  const endOfForecastX = resolvedCapMs != null && dist ? xAtMs(resolvedCapMs) : null;
  const showEndOfForecast =
    endOfForecastX != null && endOfForecastX > -10 && endOfForecastX < vw + 10;
  // δ = half-width of each cubic-bezier transition (in px).
  // 2 → total transition ~4px. Halved from the original 4 per
  // design feedback that the rounded shoulder was visually
  // dominant relative to the segment length on tight cadences.
  const TRANSITION_HALF = 2;
  /**
   * Turn one outcome's `{atMs, p}` moments into drawable paths.
   *
   * Parameterised because more than one line can be on screen at once: hovering a
   * different outcome cross-fades, so the outgoing line keeps drawing (dimmed) while
   * the incoming one fades in. Both are the SAME geometry, just different data.
   */
  const traceFor = (moms) => {
    // Build checkpoints — each is a constant-probability segment from one moment to
    // the next; the final moment's segment extends to "today", where the trace ends.
    const checkpoints = [];
    for (let i = 0; i < moms.length; i++) {
      const startMs = moms[i].atMs;
      let endMs = i + 1 < moms.length ? moms[i + 1].atMs : todayMs;
      const p = moms[i].p;
      if (!Number.isFinite(p)) continue;
      // When rewound, drop segments that start after the cap and clamp the one
      // straddling it, so the line ends exactly at the viewed snapshot moment.
      if (capMs != null) {
        if (startMs > capMs) continue;
        if (endMs > capMs) endMs = capMs;
      }
      checkpoints.push({ startMs, endMs, p });
    }
    // Performance: at high reforecast cadence (hourly → ~120
    // segments / 5 days) we batch same-color segments into a
    // single <path> d-string with multiple M/L/C subpaths so
    // React renders 2–3 path nodes instead of 100+.
    const dByColor = {};
    const append = (color, d) => {
      dByColor[color] = (dByColor[color] || "") + " " + d;
    };
    // Color follows the forecast LEVEL, not the slope: GREEN above 50%, RED below,
    // neutral exactly at 50%. (C.blueTeam is the app-wide "YES/positive" color — it
    // renders green in the customer themes; C.redTeam is red. We intentionally use
    // the team tokens, NOT C.green, because C.green is remapped to lavender in the
    // very themes where blueTeam becomes green.)
    // Green-above-half / red-below is a YES/NO reading, and it only means anything on
    // the P(YES) line. A bucket among many carries no valence — and with five outcomes a
    // front-runner at 40% would render red, reading as failing while it is winning — so a
    // per-outcome line takes the accent its own bar is drawn in.
    // Named so the single-colour path below references it directly rather than calling a
    // nullary `colorForP` with a throwaway argument.
    const BIN_LINE_COLOR = C.accent;
    const colorForP = usingBins
      ? () => BIN_LINE_COLOR
      : (p) => (p > 0.5 ? C.blueTeam : p < 0.5 ? C.redTeam : C.purpleL);
    const visibleSegs = [];
    // ── Single-colour lines draw as ONE continuous path ──────────────────────
    // A per-outcome line is lavender end to end, so it needs none of the
    // subpath-batching below — and batching actively hurt it. Each horizontal and each
    // shoulder curve was its own `M` subpath, and with round caps every abutment grew a
    // ~1px nub. Harmless at wide spacing; at a daily cadence zoomed out, segments are
    // only a few px wide, so the fixed 4px shoulder overran its neighbours and every
    // level change sprouted a little horizontal stub past the step.
    //
    // One continuous path has no abutments at all, and the shoulder is now bounded by
    // the room actually available on both sides — so it shrinks to a clean vertical step
    // when the vintages are tight instead of overrunning them.
    //
    // The builder lives in lib/forecast/stepPath.js: what it emits, and what it
    // refuses to emit (zero-length commands, collapsed-control-point cubics), is the
    // half of the no-spur invariant that the linecap/linejoin choice below completes.
    if (usingBins) {
      const color = BIN_LINE_COLOR;
      const { d, segs } = buildStepPath({
        checkpoints,
        xAtMs,
        yAtProb,
        vw,
        shoulder: TRANSITION_HALF,
      });
      for (const seg of segs) visibleSegs.push({ ...seg, color });
      if (d) dByColor[color] = d;
      return { dByColor, visibleSegs };
    }
    const spans = collapseNarrowReversals(checkpoints, xAtMs, 2 * TRANSITION_HALF);
    for (let i = 0; i < spans.length; i++) {
      const cp = spans[i];
      const next = spans[i + 1];
      const y = yAtProb(cp.p);
      const color = colorForP(cp.p);
      const xStart = xAtMs(cp.startMs);
      const xEnd = xAtMs(cp.endMs);
      // Skip segments entirely off-screen (left or right).
      if (xEnd < -20 || xStart > vw + 20) {
        continue;
      }
      // SHARP edges (default butt linecap) so the leftmost
      // segment starts EXACTLY at xStart (the GENERATED line)
      // with no round-cap overshoot. For interior segments we
      // pull both ends inward by TRANSITION_HALF — those edges
      // belong to the cubic-bezier transition curves below.
      const xSegStart = i === 0 ? xStart : xStart + TRANSITION_HALF;
      const xSegEnd = next ? xEnd - TRANSITION_HALF : xEnd;
      if (xSegEnd > xSegStart) {
        append(
          color,
          `M ${xSegStart.toFixed(1)} ${y.toFixed(1)} L ${xSegEnd.toFixed(1)} ${y.toFixed(1)}`,
        );
      }
      // Transition to the next segment (if any) — cubic bezier.
      // Curve controls are placed horizontally above/below the
      // midpoint so the curve eases in/out symmetrically.
      if (next) {
        const yNext = yAtProb(next.p);
        const xT0 = xEnd - TRANSITION_HALF;
        const xT1 = xEnd + TRANSITION_HALF;
        const xMid = xEnd;
        // When the two segments are on the SAME side of 50%, the whole connector is
        // that side's color. When they STRADDLE 50%, the connector crosses the spine
        // (yAtProb(0.5) === SPINE_Y): split it there so the above-50% half is green
        // and the below-50% half is red — color tracks LEVEL across the crossing, not
        // the outgoing segment. Non-straddling transitions keep the original single
        // curve verbatim.
        // Only meaningful on the P(YES) line: a per-outcome line is one color throughout,
        // and 50% is not a boundary for it, so it never splits at the spine.
        const straddles = !usingBins && (cp.p - 0.5) * (next.p - 0.5) < 0;
        if (straddles) {
          // First half: outgoing side, easing from (xT0, y) to the spine above xMid.
          append(
            color,
            `M ${xT0.toFixed(1)} ${y.toFixed(1)} C ${xMid.toFixed(1)} ${y.toFixed(1)} ${xMid.toFixed(1)} ${SPINE_Y.toFixed(1)} ${xMid.toFixed(1)} ${SPINE_Y.toFixed(1)}`,
          );
          // Second half: incoming side, easing from the spine to (xT1, yNext).
          append(
            colorForP(next.p),
            `M ${xMid.toFixed(1)} ${SPINE_Y.toFixed(1)} C ${xMid.toFixed(1)} ${SPINE_Y.toFixed(1)} ${xMid.toFixed(1)} ${yNext.toFixed(1)} ${xT1.toFixed(1)} ${yNext.toFixed(1)}`,
          );
        } else {
          append(
            color,
            `M ${xT0.toFixed(1)} ${y.toFixed(1)} C ${xMid.toFixed(1)} ${y.toFixed(1)} ${xMid.toFixed(1)} ${yNext.toFixed(1)} ${xT1.toFixed(1)} ${yNext.toFixed(1)}`,
          );
        }
      }
      visibleSegs.push({ i, cp, color, y, xStart, xEnd, xSegStart, xSegEnd });
    }
    return { dByColor, visibleSegs };
  };
  const { dByColor, visibleSegs } = traceFor(moments);
  if (Object.keys(dByColor).length === 0) return null;
  const colorEntries = Object.entries(dByColor);
  // EVERY other outcome's history, drawn faintly behind the highlighted one. A single
  // line in isolation says how an outcome moved but not whether it moved relative to
  // the rest — 40% reads very differently when the pack is at 10% than when it is at
  // 35%. The faint lines are that context.
  //
  // Bounded by the buckets the marker actually draws, so this is one trace per bar on
  // screen, and the highlighted one is skipped (it is drawn separately, on top).
  const contextLines = usingBins
    ? (selectionDist?.buckets ?? [])
        .filter((b) => typeof b?.label === "string" && b.label !== binLabel)
        .map((b) => ({
          label: b.label,
          entries: Object.entries(traceFor(seriesForLabel(distMoments, b.label)).dByColor),
        }))
        .filter((t) => t.entries.length > 0)
    : [];
  // Shared by the active line and every ghost: same geometry, same stroke, so the only
  // difference between them is the opacity their group animates through.
  const renderTrace = (entries) =>
    entries.map(([color, d]) => (
      <path
        key={color}
        d={d.trim()}
        stroke={color}
        strokeOpacity={0.5}
        strokeWidth="2"
        // Caps and joins differ by trace, because the two traces have opposite needs.
        //
        // The MULTI-COLOUR (P(YES)) trace is many separate sub-paths batched by colour
        // (each horizontal segment + each transition curve is its own M-subpath, and a
        // 50%-crossing splits into two coloured halves meeting at the spine). With butt
        // caps those square-ended pieces abut and read as "overlapped different lines"
        // at every corner and colour change. Round caps + joins let each piece's end
        // blend into its neighbour's start (a round cap of radius width/2 covers the
        // 2px-wide abutment), so direction changes and green↔red crossings render as one
        // continuous line. Trade-off: the leftmost (generation) end overshoots xStart by
        // ~1px — negligible.
        //
        // The SINGLE-COLOUR (per-outcome) trace is one continuous path with no
        // abutments to hide, so a round cap could only ever ADD ink beyond the
        // geometry — and it did: wherever a step's vertical ended a run (a trace clamped
        // to its resolution a moment after the last vintage) or reversed straight back
        // (two vintages inside one pixel column), the cap/join put half a stroke width
        // of ink past the level, the "little up turn" past the horizontal. Butt caps and
        // MITER joins can never exceed the path: a miter tip at a 90° step corner lands
        // exactly where the two outer edges cross, and a reversal exceeds the default
        // miter limit and bevels instead of bulging.
        strokeLinecap={usingBins ? "butt" : "round"}
        strokeLinejoin={usingBins ? "miter" : "round"}
        fill="none"
      />
    ));
  return (
    <>
      <svg
        key={`probline-${active?.id || ""}`}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 4,
          overflow: "visible",
        }}
      >
        {/* The end-of-forecast line, under every trace: it is a backdrop for where the
          line stops, not a mark that should cover the line's own last pixels.
          2px at the `44` alpha the TODAY and FORECAST lines carry — but GREY, not the
          accent: on an early resolve this line and the FORECAST line are both on screen
          at once, and in the same colour the pair read as two of the same thing rather
          than "where it actually ended" beside "where it was due to close". */}
        {showEndOfForecast && (
          <line
            data-testid="end-of-forecast-line"
            x1={endOfForecastX}
            x2={endOfForecastX}
            y1={iconCenterAt1 - bandH * BAND_OVERHANG}
            y2={iconCenterAt0 + bandH * BAND_OVERHANG}
            stroke={C.gray}
            strokeOpacity={0x44 / 255}
            strokeWidth="2"
          />
        )}
        {/* Context lines first, so the highlighted one draws OVER them and is never
          occluded by an outcome the user is not looking at. */}
        {contextLines.map((t) => (
          <g key={`bin-${t.label}`} className="bin-line bin-line--faint">
            {renderTrace(t.entries)}
          </g>
        ))}
        {/* The highlighted line. Keyed by LABEL (not remounted per hover) so moving
          between outcomes transitions both groups' opacity instead of snapping. In
          non-bin mode there is only ever this one line and no highlight state. */}
        <g
          key={usingBins ? `bin-${binLabel}` : "prob-line"}
          className={usingBins ? "bin-line bin-line--lead" : undefined}
        >
          {renderTrace(colorEntries)}
        </g>
        {/* On-line micro-ticks removed — the date-axis tick row
          below the spine now carries the "forecast was made"
          marker, which keeps the probability line itself
          clean and gives the marker a stable horizontal
          anchor independent of the line's Y position. */}
      </svg>
      {/* (The per-moment white canvas extension was removed — the full-height
        GOLD snapshot guide line rendered at the App container level now spans
        the probability line, axis, and signals in one piece, so a separate
        white half-line here would double up and clash.) */}
      {/* Invisible hover overlays — one per visible segment.
        Each is a 12px-tall band
        centered on the segment's Y. Hovering sets a hov key
        that renders the tooltip below. Kept thin (12px) so
        they don't shadow neighboring event chips' hover
        zones. */}
      {visibleSegs.map((seg) => {
        const xL = Math.max(0, seg.xStart);
        const xR = Math.min(vw, seg.xEnd);
        const w = Math.max(1, xR - xL);
        return (
          <div
            key={`probHover-${seg.i}`}
            onMouseEnter={(e) => {
              setHov(`probline::${seg.i}`);
              setHovPos({ x: e.clientX, y: e.clientY });
            }}
            onMouseMove={(e) => setHovPos({ x: e.clientX, y: e.clientY })}
            onMouseLeave={() =>
              setHov((prev) =>
                typeof prev === "string" && prev === `probline::${seg.i}` ? null : prev,
              )
            }
            style={{
              position: "absolute",
              left: xL,
              top: seg.y - 6,
              width: w,
              height: 12,
              cursor: "pointer",
              zIndex: 5,
            }}
          />
        );
      })}
      {/* Hover tooltip — matches the TSD signal-library style:
        bare colored text, no border/box. Single-line
        "Probability X%" so it reads cleanly inline with the
        cursor. */}
      {hov &&
        typeof hov === "string" &&
        hov.startsWith("probline::") &&
        (() => {
          const idx = parseInt(hov.slice("probline::".length), 10);
          const seg = visibleSegs.find((s) => s.i === idx);
          if (!seg) return null;
          const { color } = seg;
          const p = seg.cp.p;
          const pPct = fmtPct(p);
          // Name the outcome on a per-outcome line: "Probability 42%" is ambiguous the
          // moment more than one line could be drawn here.
          const binBucket = selectionDist?.buckets?.find((b) => b?.label === binLabel);
          const readout = usingBins
            ? `${binBucket ? bucketLabel(binBucket) : binLabel} ${pPct}%`
            : `Probability ${pPct}%`;
          return (
            <div
              style={{
                position: "fixed",
                left: hovPos.x + 14,
                top: hovPos.y - 10,
                zIndex: 200,
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color,
                  fontFamily: '"IBM Plex Mono",monospace',
                  whiteSpace: "nowrap",
                  opacity: 0.9,
                }}
              >
                {readout}
              </div>
            </div>
          );
        })()}
    </>
  );
}
